/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./MissionDetails.css";
import ViewMission from "./component/ViewMission";
import { simpleFetch } from "../../restWrapper";
import queryString from "query-string";
import Promise from "bluebird";
import { PAYMENT_MINIMUM, DEFAULT_LANGUAGE_ARRAY, PLATFORM } from "../../config";
import PropTypes from "prop-types";
import ExpressPayment from "./../ExpressPayment/ExpressPayment.js";
import { getProjectDescription } from "../../assets/projectContentHelper.js";
import moment from "moment";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { Helmet } from "react-helmet";
import { rememberOrgTitle } from "../../assets/orgTitle";
import { getOrganizationID, validatePlatformMatch, getEffectiveFeeCoverageMode, withoutExpressPaymentAuthorisation, isPaymentMethodHiddenOnProject, organizationHasPayMe, PROJECT_EXPRESS_KEY } from "../../assets/helper.js";

// Note on withoutExpressPaymentAuthorisation (see assets/helper.js): it is applied
// to BOTH the constructor's initial state and the componentDidMount restore below.
// setState merges rather than replaces, so stripping only at the restore would leave
// whatever the constructor had already spread in - which is exactly how a first
// attempt at the CP-253 fix silently did nothing.

class MissionDetails extends Component {
  constructor(props) {
    super(props);

    const subdomain = getOrganizationID(window.location.href).subdomain;
    const domain = getOrganizationID(window.location.href).domain;
    const isCustomDomain = getOrganizationID(window.location.href).isCustomDomain;

    const query = queryString.parse(this.props.location.search);

    // Only redirect if 'no' parameter is missing (project ID is required)
    // Don't redirect just because location.state is missing - we can fetch org data
    if (!query.no) {
      // this.state must be set before returning - history.push() doesn't
      // unmount synchronously, so render() still runs at least once and
      // would throw on this.state.loading with no state assigned.
      this.state = { loading: true };
      this.props.history.push({ pathname: "/SelectMission" });
      return;
    }

    let lngArr = DEFAULT_LANGUAGE_ARRAY;
    lngArr.push("default");

    let originalDate = new Date();
    originalDate.setDate(originalDate.getDate());

    const locationState = withoutExpressPaymentAuthorisation(this.props.location.state);
    const initialCoverFee = typeof locationState.coverFee === 'boolean' ? locationState.coverFee : false;

    // Check if frequency is a recurring type
    const isRecurringFrequency = locationState.frequency === "WEEKLY" ||
                                  locationState.frequency === "BIWEEKLY" ||
                                  locationState.frequency === "MONTHLY";

    this.state = {
      ...locationState,
      amount: 0,
      scheduledDate: moment().toISOString(false),
      language: this.props.language,
      minDate: originalDate,
      startDate: originalDate,
      loading: true,
      isRecurring: false,
      recurringOccurrences: 0,
      isInfiniteRecurring: isRecurringFrequency ? true : false,
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      projectId: query.no,
      lngArr: lngArr,
      frequency: "ONETIME",
      receipt: false,
      showModal: false,
      coverFee: initialCoverFee,
      isTransitioning: false,
      feeCoverageEstimate: null,
      feeCoverageEstimateAmex: null,
      feeCoverageEstimatePayMe: null,
      feeCoverageEstimateLoading: false,
    };
    this.onBtnClicked = this.onBtnClicked.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleReceipt = this.handleReceipt.bind(this);
    this.handleCoverFeeToggle = this.handleCoverFeeToggle.bind(this);
    this.navigateToPersonalDetails = this.navigateToPersonalDetails.bind(this);
    this.initializePaymentRequest = this.initializePaymentRequest.bind(this);

    this.transitionDelay = 320; // Synchronized with PersonalDetails slide-in animation
    this.transitionTimeout = null;
    this._feeEstimateTimer = null;
    // Guard for the wallet-support probe. Deliberately an instance field, NOT
    // state - see initializePaymentRequest() for why that distinction matters.
    this._paymentRequestProbed = false;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    window.addEventListener("scroll", this.onScroll);

    // Explicitly check for "no-project" and redirect immediately
    if (this.state.projectId === "no-project") {
      this.props.history.push({ pathname: "/SelectMission" });
      return; // Stop further execution in componentDidMount
    }

    // If we have state from navigation, use it directly
    if (this.props.location.state && this.props.location.state.mission) {
      // Check if description is missing (null) - if so, refetch the project
      const needsDescriptionRefetch = !this.props.location.state.mission.description;
      // Always refetch organization data to ensure colors are up to date
      const needsOrgRefetch = true;

      // Debug logging
      // console.log("MissionDetails: Restoring state from navigation");
      // console.log("MissionDetails: amount from location.state:", this.props.location.state.amount, "type:", typeof this.props.location.state.amount);
      // console.log("MissionDetails: frequency from location.state:", this.props.location.state.frequency);
      // console.log("MissionDetails: recurringOccurrences from location.state:", this.props.location.state.recurringOccurrences);

      this.setState({
        ...withoutExpressPaymentAuthorisation(this.props.location.state),
        loading: false // Don't show loading screen, show content immediately
      });

      // Refetch data in the background if needed
      if (needsDescriptionRefetch || needsOrgRefetch) {
        Promise.all([
          needsDescriptionRefetch ? this.getProject() : Promise.resolve({ status: 200 }),
          needsOrgRefetch ? this.getOrgData() : Promise.resolve({ status: 200 })
        ]).then((results) => {
          results.forEach((res, index) => {
            if (res.status !== 200) {
              // console.error(`Failed to refetch data (${index === 0 ? 'project' : 'organization'}):`, res);
            }
          });
        }).catch((error) => {
          // Background refresh only - the page is already rendered, so swallow
          // the failure rather than letting it surface as an unhandled rejection.
          // console.error("Failed to refetch MissionDetails data:", error);
        });
      }

      // Still setup payment request for Apple/Google Pay
      this.initializePaymentRequest();
    } else {
      // Otherwise fetch data as usual
      const tomorrow = moment().add(1, 'days').startOf('day');
      this.setState({
        startDate: tomorrow.toDate(),
        // FIX: For one-time payments, scheduledDate should be "now", not tomorrow
        // The initial state already sets scheduledDate to now (line 48)
        // Only recurring payments should start tomorrow
        // scheduledDate will be updated if user selects recurring payment
      });

      this.initializePaymentRequest();

      //    var Promise = require('es6-promise').Promise;
      Promise.all([
        this.getOrgData(),
        this.getProject(),
        this.getDonorData(),
      ]).then((res) => {
        this.setState({
          loading: false,
        });
        // Check results, redirect to SelectMission on any fetch failure
        for (var i = 0; i < res.length; i++) {
          if (res[i].status !== 200) {
            // console.error("Fetch failed in MissionDetails Promise.all:", res[i]);
            this.props.history.push({ pathname: "/SelectMission" });
            return;
          }
        }
      }).catch((error) => {
        // Without this the page hangs on the loading skeleton forever: `loading`
        // is only cleared inside the .then() above, so anything that throws while
        // handling a response (an unexpected body shape, an unparseable project
        // description) would leave the donor staring at a spinner with no error
        // and no way out. Fail visibly instead, matching the non-200 path.
        // console.error("Error loading MissionDetails:", error);
        this.setState({
          loading: false,
        });
        this.props.history.push({ pathname: "/SelectMission" });
      });
    }
  }

  initializePaymentRequest() {
    // `this.props.stripe` comes from <Elements> via useStripe(). Elements always
    // starts with a null stripe and resolves it in a passive effect, which React
    // runs AFTER componentDidMount - so the probe below can never succeed at mount
    // time. componentDidUpdate re-invokes this once stripe actually arrives.
    if (!this.props.stripe || this._paymentRequestProbed) {
      return;
    }
    this._paymentRequestProbed = true;

    // This request exists ONLY to ask the browser whether a wallet is available
    // (canMakePayment). It is never charged - ExpressPayment builds its own
    // request with the donor's real amount when the modal opens - so a fixed,
    // always-valid amount is used here.
    const paymentRequest = this.props.stripe.paymentRequest({
      country: "HK",
      currency: "hkd",
      total: {
        label: "Payment",
        amount: Math.round(PAYMENT_MINIMUM * 100),
      },
    });

    // Store ONLY the boolean. The PaymentRequest object holds functions, and
    // this.state is spread into history.push() state on every navigation out of
    // this page; functions are not structured-cloneable, so putting it in state
    // throws DataCloneError and silently breaks navigation (CP-250 regression).
    paymentRequest.canMakePayment().then((result) => {
      this.setState({ canMakePayment: !!result });
    }).catch(() => {
      // Wallet unavailable or the probe failed - leave canMakePayment false so
      // the donor simply continues through the standard payment flow.
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Stripe loads asynchronously; run the wallet probe as soon as it lands.
    if (!prevProps.stripe && this.props.stripe) {
      this.initializePaymentRequest();
    }

    const effectiveMode = getEffectiveFeeCoverageMode(
      this.state.organization?.feeCoverageMode,
      this.state.mission?.feeCoverageMode,
    );
    const prevEffectiveMode = getEffectiveFeeCoverageMode(
      prevState.organization?.feeCoverageMode,
      prevState.mission?.feeCoverageMode,
    );

    // Sync coverFee whenever effectiveMode resolves to required/disabled.
    // Done here (not in a post-fetch callback) because React 18 batches setState
    // inside Promise.then; reading this.state in the parent Promise.all().then
    // returns stale data, so we wait for state to flush and react to the change.
    if (effectiveMode !== prevEffectiveMode) {
      if (effectiveMode === 'required' && !this.state.coverFee) {
        this.setState({ coverFee: true });
      } else if (effectiveMode === 'disabled' && this.state.coverFee) {
        this.setState({ coverFee: false });
      }
    }

    // Show estimate when org has the toggle on OR the project forces fees on
    // (in which case donors deserve transparency regardless of the org toggle).
    const shouldShowEstimate = this.state.organization?.showFeeCoverageAmount || effectiveMode === 'required';
    const wasShowEstimate = prevState.organization?.showFeeCoverageAmount || prevEffectiveMode === 'required';
    if (shouldShowEstimate) {
      const justEnabled = !wasShowEstimate;
      if (justEnabled || prevState.amount !== this.state.amount || prevState.coverFee !== this.state.coverFee) {
        if (this._feeEstimateTimer) clearTimeout(this._feeEstimateTimer);
        // Flip loading on as soon as the input changes so the skeleton shows
        // during the debounce window, not just during the network round-trip.
        const willFetch = this.state.amount > 0 && this.state.coverFee;
        if (willFetch && !this.state.feeCoverageEstimateLoading) {
          this.setState({ feeCoverageEstimateLoading: true });
        } else if (!willFetch && this.state.feeCoverageEstimateLoading) {
          this.setState({ feeCoverageEstimateLoading: false });
        }
        this._feeEstimateTimer = setTimeout(() => this.fetchFeeCoverageEstimate(), 400);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
    if (this._feeEstimateTimer) {
      clearTimeout(this._feeEstimateTimer);
    }
  }

  async fetchFeeCoverageEstimate() {
    const { amount, organization, coverFee } = this.state;
    if (!amount || amount <= 0 || !organization?.organizationId) {
      this.setState({ feeCoverageEstimateLoading: false });
      return;
    }
    if (!coverFee) {
      this.setState({ feeCoverageEstimate: null, feeCoverageEstimateAmex: null, feeCoverageEstimatePayMe: null, feeCoverageEstimateLoading: false });
      return;
    }
    const baseParams = {
      amount: Number(amount),
      organizationId: organization.organizationId,
      projectId: this.state.projectId,
      transactionCountry: "HK",
      cardCountry: "HK",
      paymentMethod: "creditCard",
      coverFee: true,
    };
    // PayMe carries the org's own rate and no platform service fee, so it is
    // priced separately rather than assumed to match a card. A campaign that
    // hides PayMe is quoted on cards alone (CP-281, CP-280).
    const wantPayMe =
      organizationHasPayMe(organization) &&
      !isPaymentMethodHiddenOnProject(this.state.mission, 'payme');
    try {
      const [res, resAmex, resPayMe] = await Promise.all([
        simpleFetch("/user/payments/calculate-fees", "POST", baseParams),
        simpleFetch("/user/payments/calculate-fees", "POST", { ...baseParams, cardBrand: "amex" }),
        wantPayMe
          ? simpleFetch("/user/payments/calculate-fees", "POST", { ...baseParams, paymentMethod: "PayMe", cardBrand: "PayMe" })
          : Promise.resolve(null),
      ]);
      const read = (r) => (r && r.status === 200 && r.body?.totalFee != null ? r.body : null);
      this.setState({
        feeCoverageEstimate: read(res),
        feeCoverageEstimateAmex: read(resAmex),
        feeCoverageEstimatePayMe: read(resPayMe),
        feeCoverageEstimateLoading: false,
      });
    } catch (e) {
      this.setState({ feeCoverageEstimate: null, feeCoverageEstimateAmex: null, feeCoverageEstimatePayMe: null, feeCoverageEstimateLoading: false });
    }
  }

  getDonorData() {
    if (!this.props.isAuth) {
      return {
        status: 200,
      };
    }
    return simpleFetch("/donors", "GET").then((res) => {
      if (res.status !== 200) {
        return res.status;
      }

      let dataObj = res.body;
      if (!dataObj) {
        // console.error("Donor response had no body:", res);
        return { status: 500, body: res.body };
      }

      let donorInfo = {
        firstName: dataObj.firstName,
        lastName: dataObj.lastName,
        email: dataObj.username,
      };
      if (dataObj.phoneNumber) donorInfo.phoneNumber = dataObj.phoneNumber;

      this.setState({
        ...this.state,
        ...{
          donorInfo,
        },
      });
      return res;
    });
  }

  getOrgData() {
    return simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      if (res.status !== 200) return res;

      // A 200 with an unexpected body used to throw here and hang the page.
      // Report it as a failed fetch so the caller's existing handling applies.
      let orgObj = res.body && res.body.organizationObj;
      if (!orgObj) {
        // console.error("Organization response missing organizationObj:", res.body);
        return { status: 500, body: res.body };
      }
      rememberOrgTitle(orgObj.organizationName);

      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        orgObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        // console.error("Cannot display mission: platform mismatch");
        this.setState({ fetching: false });
        this.props.history.push({
          pathname: "/" + this.props.language
        });
        return res;
      }

      // Ensure paymentMethodCode exists
      if (!orgObj.paymentMethodCode) {
        orgObj.paymentMethodCode = {};
      }

      // Set organizationId & organizationViewId explicitly
      orgObj.organizationId = res.body._id;
      orgObj.organizationViewId = res.body.id;

      const logoStyle = {
        backgroundImage: "url('" + orgObj.logo + "')",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        border: "none",
      };

      const orgMinimum = orgObj.minimumPaymentAmount;
      const minimum = orgMinimum ? orgMinimum : PAYMENT_MINIMUM;
      orgObj.minimumPaymentAmount = minimum;

      orgObj.logo = logoStyle;
      // Only update organization-related fields, don't spread this.state
      // to avoid race conditions that could overwrite amount and other fields.
      // coverFee is synced in componentDidUpdate once effectiveMode resolves.
      this.setState({
        organization: orgObj,
        paymentMethod: res.body.paymentMethod,
      });

      return res;
    });
  }

  getProject() {
    return simpleFetch("/projects/" + this.state.projectId, "GET").then(
      (res) => {
        if (res.status !== 200) {
          return res;
        }

        let missionObj = res.body;
        if (!missionObj) {
          // console.error("Project response had no body:", res);
          return { status: 500, body: res.body };
        }

        const headerBackgroundPicture = missionObj.headerBackgroundPicture;
        const headerImg = headerBackgroundPicture
          ? headerBackgroundPicture.split(",")
          : null;
        missionObj.headerBackgroundPicture = headerImg;
        let lngArr = DEFAULT_LANGUAGE_ARRAY;
        lngArr.push("default");

        if (missionObj.description) {
          const contentObj = missionObj.description;
          try {
            missionObj.description = getProjectDescription(
              this.props.language,
              contentObj,
              lngArr,
              "object",
              false
            );
          } catch (error) {
            // A malformed or unexpected stored description should cost the donor
            // the description text, not the entire project page.
            // console.error("Failed to parse project description:", error);
            missionObj.description = null;
          }
        }
        // Only update mission field, don't spread this.state
        // to avoid race conditions that could overwrite amount and other fields.
        // coverFee is synced in componentDidUpdate once effectiveMode resolves.
        this.setState({ mission: missionObj });

        if (res.body.status !== "open" && res.body.status !== "unlisted") {
          this.props.history.push("/SelectMission");
          return res;
        }
        return res;
      }
    );
  }

  onBtnClicked() {
    const minimum = (this.state.organization && this.state.organization.minimumPaymentAmount) || 0;
    // Block proceed if fee coverage is mandatory but the estimate hasn't loaded —
    // donor must see the fee amount before continuing.
    const effectiveMode = getEffectiveFeeCoverageMode(
      this.state.organization?.feeCoverageMode,
      this.state.mission?.feeCoverageMode,
    );
    if (effectiveMode === 'required' && this.state.amount > 0 && !this.state.feeCoverageEstimate) {
      return;
    }
    if (this.props.isAuth) {
      if (this.state.amount >= minimum || this.state.validData) {
        fetch(
          "https://ipapi.co/json/?key=7331911097f0e92e5a024ac30c0dd33ae3232200",
          {
            method: "GET",
          }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error('IP API request failed');
            }
            return response.json();
          })
          .then((data) => {
            const dataObj = { ...this.state }; // Create a copy to avoid mutating state directly
            if (!data) dataObj.country = "HK";
            else dataObj.country = data.country;
            dataObj.receipt = true;
            // Determine transactionType
            const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true; // Default true
            dataObj.transactionType = missionDonation ? 'donation' : 'payment';
            // console.log(`MissionDetails (onBtnClicked - Auth) - Setting transactionType: ${dataObj.transactionType}`); // Removed log
            this.navigateToPersonalDetails(dataObj);
          })
          .catch((error) => {
            // console.error('Failed to fetch IP location, defaulting to HK:', error);
            // Default to HK if IP detection fails
            const dataObj = { ...this.state };
            dataObj.country = "HK";
            dataObj.receipt = true;
            const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true;
            dataObj.transactionType = missionDonation ? 'donation' : 'payment';
            this.navigateToPersonalDetails(dataObj);
          });
      }
    }
    if (
      (this.state.amount >= minimum || this.state.validData) &&
      !this.props.isAuth
    ) {
      if (
        this.state.canMakePayment &&
        this.state.organization &&
        (this.state.organization.currentPlan === "premium" || this.state.organization.currentPlan === "pro") &&
        !isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_EXPRESS_KEY)
      ) {
        this.setState({
          showModal: true,
        });
      } else {
        // Detect user's IP-based location for compliance and localization
        // NOTE: This is NOT the card issuer country that affects Stripe fees
        // Card issuer country determines: HK (2.9%) vs Overseas (3.4%) Stripe rates
        fetch(
          "https://ipapi.co/json/?key=7331911097f0e92e5a024ac30c0dd33ae3232200",
          {
            method: "GET",
          }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error('IP API request failed');
            }
            return response.json();
          })
          .then((data) => {
            const dataObj = { ...this.state }; // Create a copy
            if (!data) dataObj.country = "HK"; // Default to HK if IP detection fails
            else dataObj.country = data.country; // IP-based country for compliance
            dataObj.receipt = true;
            // Determine transactionType
            const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true; // Default true
            dataObj.transactionType = missionDonation ? 'donation' : 'payment';
            // console.log(`MissionDetails (onBtnClicked - No Auth) - Setting transactionType: ${dataObj.transactionType}`); // Removed log
            this.navigateToPersonalDetails(dataObj);
          })
          .catch((error) => {
            //console.error('Failed to fetch IP location, defaulting to HK:', error);
            // Default to HK if IP detection fails
            const dataObj = { ...this.state };
            dataObj.country = "HK";
            dataObj.receipt = true;
            const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true;
            dataObj.transactionType = missionDonation ? 'donation' : 'payment';
            this.navigateToPersonalDetails(dataObj);
          });
      }
    }
  }

  closeModal() {
    this.setState({
      showModal: false,
    });
  }

  handleReceipt() {
    fetch(
      "https://ipapi.co/json/?key=7331911097f0e92e5a024ac30c0dd33ae3232200",
      {
        method: "GET",
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error('IP API request failed');
        }
        return response.json();
      })
      .then((data) => {
        const dataObj = { ...this.state }; // Create a copy
        if (!data) dataObj.country = "HK";
        else dataObj.country = data.country;
        dataObj.receipt = true;
        // Determine transactionType
        const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true; // Default true
        dataObj.transactionType = missionDonation ? 'donation' : 'payment';
        // console.log(`MissionDetails (handleReceipt) - Setting transactionType: ${dataObj.transactionType}`); // Removed log
        this.navigateToPersonalDetails(dataObj);
      })
      .catch((error) => {
        // console.error('Failed to fetch IP location, defaulting to HK:', error);
        // Default to HK if IP detection fails
        const dataObj = { ...this.state };
        dataObj.country = "HK";
        dataObj.receipt = true;
        const missionDonation = dataObj.mission && typeof dataObj.mission.donation === 'boolean' ? dataObj.mission.donation : true;
        dataObj.transactionType = missionDonation ? 'donation' : 'payment';
        this.navigateToPersonalDetails(dataObj);
      });
  }

  handleCoverFeeToggle() {
    const newCoverFee = !this.state.coverFee;

    // Fee calculation removed - not needed in MissionDetails
    // Fees will be calculated by backend during payment processing
    this.setState({
      coverFee: newCoverFee,
    });
  }

  navigateToPersonalDetails(dataObj) {
    const navigationState = {
      ...dataObj,
      fromMissionDetails: true,
    };

    const startNavigation = () => {
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }
      this.transitionTimeout = window.setTimeout(() => {
        this.props.history.push({
          pathname: "/PersonalDetails",
          state: navigationState,
        });
        this.transitionTimeout = null;
      }, this.transitionDelay);
    };

    if (!this.state.isTransitioning) {
      this.setState({ isTransitioning: true }, startNavigation);
    } else {
      startNavigation();
    }
  }

  render() {
    if (this.state.loading || !this.state.organization) {
      return (
        <div className="MissionDetails-view MissionDetails-gradient">
          <div className="MissionDetails-header  MissionDetails-gradient">
            <div className="MissionDetails-logo MissionDetails-gradient"></div>

            <div className="MissionDetails-mainToolBarContainer">
              <div className="MissionDetails-bg" />
            </div>
          </div>

          <div className="MissionDetails-loadingSteps">
            <div className="MissionDetails-loadingBackContainer MissionDetails-gradient"></div>

            <div className="MissionDetails-loadingIndicator MissionDetails-gradient"></div>
          </div>

          <div className="MissionDetails-loadingOuterContainer">
            <div className="MissionDetails-loadingBodyContainer MissionDetails-gradient"></div>
          </div>

          <div className="MissionDetails-pictureandBodyContainer">
            <div className="MissionDetails-pictureandBody">
              <div className="MissionDetails-picture">
                <div className="MissionDetails-loadingPicture MissionDetails-gradient" />
              </div>
              <div className="MissionDetails-inputArea">
                <div className="MissionDetails-inputAmount">
                  <div className="MissionDetails-headingText"></div>

                  <div className="MissionDetails-loadingInputContainer MissionDetails-gradient"></div>
                </div>

                <div className="MissionDetails-loadingNextBtn MissionDetails-gradient"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <Helmet>
          <title>{this.state.organization && this.state.organization.organizationName}</title>
        </Helmet>
        <ViewMission func={this} state={this.state} backpath="/SelectMission" />

        {!!this.state.showModal &&
          this.state.organization &&
          !isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_EXPRESS_KEY) && (
          <ExpressPayment
            show={this.state.showModal}
            btnColor={`#${this.state.organization.headerColor || "000000"}`}
            handleClose={this.closeModal}
            state={this.state}
            handleReceipt={this.handleReceipt}
            history={this.props.history}
          />
        )}
      </div>
    );
  }
}

MissionDetails.propTypes = {
  isAuth: PropTypes.bool,
  language: PropTypes.string,
  stripe: PropTypes.object,
};

// Wrapper component to use Stripe hooks with class component
function MissionDetailsWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <MissionDetails {...props} stripe={stripe} elements={elements} />;
}

export default MissionDetailsWithStripe;
